import { useState, useEffect } from 'react';

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * Text input for dollar amounts. Displays formatted with commas
 * when not focused (e.g. "350,000"). On focus, shows the raw
 * number for easy editing. Commits on blur or Enter.
 */
export function CurrencyInput({
  value,
  onChange,
  disabled = false,
  className = '',
  placeholder,
}: CurrencyInputProps): React.JSX.Element {
  const [text, setText] = useState(formatDisplay(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setText(formatDisplay(value));
    }
  }, [value, focused]);

  function commit(raw: string): void {
    const cleaned = raw.replace(/[,$\s]/g, '');
    const parsed = parseFloat(cleaned);
    if (Number.isNaN(parsed) || parsed < 0) {
      onChange(0);
      setText('');
      return;
    }
    const rounded = Math.round(parsed * 100) / 100;
    onChange(rounded);
    setText(formatDisplay(rounded));
  }

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        style={{ paddingLeft: '1.5rem' }}
        onFocus={(e) => {
          setFocused(true);
          e.target.select();
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => {
          setFocused(false);
          commit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}

function formatDisplay(n: number): string {
  if (n === 0) return '';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
